import { detectExternalLink, makeDocsLink } from '../../lib/docs/utils';
import Link from 'next/link';
import clsx from 'clsx';
import Image from 'next/image';

type CommonProps = {
  title: string;
  description: string;
  url: string;
};

type ConditionalPropsWithIconComponent = {
  IconComponent: (props: React.ComponentProps<'svg'>) => JSX.Element;
  logoIcon?: never;
  logoSize?: never;
};

type ConditionalPropsWithIconLogo = {
  IconComponent?: never;
  logoIcon: string;
  logoSize: 'sm' | 'md';
};

type Props = CommonProps &
  (ConditionalPropsWithIconComponent | ConditionalPropsWithIconLogo);

export function Card({
  url,
  title,
  description,
  IconComponent,
  logoIcon,
  logoSize
}: Props) {
  const href = detectExternalLink(url) ? url : makeDocsLink(url);

  {
    /* TODO: These colors need to be updated to use the theme() function to point the updated tailwind.config colors once they are finished by design team  */
  }
  return (
    <Link
      href={href}
      target={detectExternalLink(url) ? '_blank' : '_self'}
      className={clsx(
        'group relative block space-y-4 rounded-2xl bg-white p-6 shadow-lg ring-1 ring-gray-950/[0.05] transition dark:bg-[#1F2024] dark:ring-0',
        'after:pointer-events-none after:z-50 after:rounded-2xl after:dark:absolute after:dark:inset-0 after:dark:shadow-[inset_0px_1px_0px_rgba(255,255,255,0.06),inset_0px_-1px_0px_rgba(255,255,255,0.02),inset_1px_0px_0px_rgba(255,255,255,0.03),inset_-1px_0px_0px_rgba(255,255,255,0.03)]'
      )}
    >
      <div className="space-y-4">
        {logoIcon && (
          <div
            className={clsx(
              'relative h-6 duration-[450ms] group-hover:-translate-y-0.5',
              {
                'w-6': logoSize === 'sm',
                'w-16': logoSize === 'md'
              }
            )}
          >
            <Image
              src={logoIcon}
              alt={`${title} logo`}
              sizes={
                logoSize === 'sm'
                  ? '24px'
                  : logoSize === 'md'
                  ? '40px'
                  : '100vw'
              }
              fill
            />
          </div>
        )}
        {IconComponent && (
          <IconComponent className="aspect-square h-6 text-purple-500 duration-[450ms] group-hover:-translate-y-0.5" />
        )}
        <h3 className="text-lg tracking-tight text-black dark:text-white">
          {title}
        </h3>
      </div>
      <p className="text-sm/6 tracking-tight text-black/60 dark:text-white/60">
        {description}
      </p>
    </Link>
  );
}
